import { useCallback } from "react";
import { apiClient } from "module/api";
import { useStore } from "store";
import { useVariables } from "../context/VariablesContext";
import type { VariableFile, ColumnMapping } from "../types";

/**
 * Hook that encapsulates all variable-related API calls with loading/error states.
 * Gets clientId from Zustand store (client.id or user.clientId).
 */
export const useVariablesApi = () => {
  const { client, user } = useStore();
  const clientId = client?.id || user?.clientId?.toString() || null;
  const { setExistingVariableFiles, setIsLoading, setVariableData } = useVariables();

  /**
   * Fetches existing variable files for the current client
   */
  const fetchVariables = useCallback(async (): Promise<VariableFile[]> => {
    if (!clientId) return [];
    setIsLoading(true);
    try {
      const data = await apiClient.getClientVariables(clientId);

      const files: VariableFile[] = (data || []).map((v: any) => ({
        id: v.id,
        name: v.name,
        csvUrl: v.csv_url || v.csvUrl,
        csvColumns: v.csv_columns || v.csvColumns || [],
        clientId: v.client_id || v.clientId,
        metadata: v.metadata,
        createdAt: v.created_at || v.createdAt,
      }));
      setExistingVariableFiles(files);
      return files;
    } catch (error) {
      console.error("Error fetching variables:", error);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [clientId, setExistingVariableFiles, setIsLoading]);

  /**
   * Uploads a variable file to S3 and creates a variable record via API.
   * @param csvBlob - The CSV blob to upload (always CSV, even if source was XLSX)
   * @param fileName - Original file name
   * @param columnMappings - Column-to-variable name mappings from the mapping modal
   */
  /**
   * Uploads a variable file to S3 and creates a variable record via API.
   * Follows the same flow as platform-next's campaign tracking upload:
   * 1. Get presigned URL from /variable/document-presigned-url
   * 2. PUT file to S3 (no Content-Type header — presigned URL only signs 'host')
   * 3. POST to /variable to create the DB record with metadata
   *
   * @param csvBlob - The CSV blob to upload (always CSV, even if source was XLSX)
   * @param fileName - Original file name
   * @param columnMappings - Column-to-variable name mappings from the mapping modal
   * @param parsedData - Parsed CSV data rows for metadata (headers, firstRow, rowsAmount)
   */
  const uploadVariable = useCallback(
    async (
      csvBlob: Blob,
      fileName: string,
      columnMappings: ColumnMapping[],
      parsedData?: Record<string, string>[],
      totalRows?: number,
      abortSignal?: AbortSignal
    ): Promise<VariableFile | null> => {
      if (!clientId) {
        console.error("uploadVariable aborted: clientId is undefined/null. Check route params.");
        return null;
      }
      setIsLoading(true);
      try {
        // Step 1: Get presigned URL
        const csvFileName = fileName.replace(/\.xlsx?$/i, ".csv");

        const presignedRes = await apiClient.getVariablePresignedUrl({
          fileName: csvFileName,
          clientId: Number(clientId),
        });
        if (!presignedRes.ok) {
          const errText = await presignedRes.text().catch(() => "");
          console.error("Presigned URL failed:", presignedRes.status, errText);
          throw new Error(`Failed to get presigned URL: ${presignedRes.status}`);
        }
        const presignedData = await presignedRes.json();
        const { url: presignedUrl, dbURL } = presignedData;

        // Step 2: Upload to S3
        // IMPORTANT: Send blob without Content-Type to avoid CORS preflight.
        // The presigned URL only signs 'host', so extra headers cause signature mismatch.
        // Strip the blob type by creating a new Blob without a type.
        const rawBlob = new Blob([await csvBlob.arrayBuffer()]);
        const s3Res = await fetch(presignedUrl, {
          method: "PUT",
          body: rawBlob,
          signal: abortSignal,
        });
        if (!s3Res.ok) {
          const s3Err = await s3Res.text().catch(() => "");
          console.error("S3 upload failed:", s3Res.status, s3Err);
          throw new Error(`Failed to upload file to S3: ${s3Res.status}`);
        }

        // Step 3: Create variable record in DB
        const variableName = fileName.replace(/\.(csv|xlsx?)$/i, "");
        const csvColumns = columnMappings.map((m) => m.variableName);
        const headers = columnMappings.map((m) => m.originalName);
        const firstRow = parsedData && parsedData.length > 0 ? headers.map((h) => parsedData[0][h] || "") : [];

        const createPayload = {
          name: variableName,
          csv_url: dbURL,
          csv_columns: csvColumns,
          client_id: Number(clientId),
          metadata: {
            headers,
            firstRow,
            rowsAmount: totalRows || parsedData?.length || 0,
            fileUrl: dbURL,
            columnMappings,
          },
        };

        const createRes = await apiClient.createVariable(createPayload);
        if (!createRes.ok) {
          const createErr = await createRes.text().catch(() => "");
          console.error("Create variable failed:", createRes.status, createErr);
          throw new Error(`Failed to create variable record: ${createRes.status}`);
        }
        const created = await createRes.json();

        const variableFile: VariableFile = {
          id: created.id,
          name: created.name,
          csvUrl: created.csv_url || dbURL,
          csvColumns: created.csv_columns || csvColumns,
          clientId: created.client_id,
          metadata: created.metadata,
          createdAt: created.created_at,
        };

        // Refresh the existing files list
        await fetchVariables();
        return variableFile;
      } catch (error) {
        console.error("Error uploading variable:", error);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [clientId, setIsLoading, fetchVariables]
  );

  /**
   * Downloads and parses a CSV from a variable file's S3 URL.
   * Populates variableData in context for preview (shortest/longest).
   */
  const downloadAndParseCSV = useCallback(
    async (csvUrl: string): Promise<Record<string, string>[] | null> => {
      setIsLoading(true);
      try {
        // Get download URL

        const downloadRes = await apiClient.getVariableDownloadUrl({ url: csvUrl });
        if (!downloadRes.ok) {
          const errText = await downloadRes.text().catch(() => "");
          console.error("Download URL request failed:", downloadRes.status, errText);
          throw new Error("Failed to get download URL");
        }
        const downloadData = await downloadRes.json();
        const signedUrl = downloadData.url;

        // Fetch CSV content using streaming to avoid loading huge files into memory
        const MAX_PREVIEW_ROWS = 100;
        const csvRes = await fetch(signedUrl);
        if (!csvRes.ok) {
          console.error("CSV fetch failed:", csvRes.status);
          throw new Error("Failed to download CSV");
        }

        // Stream-read only enough data for preview
        const reader = csvRes.body?.getReader();
        let lines: string[] = [];

        if (reader) {
          const decoder = new TextDecoder();
          let accumulated = "";
          while (lines.length < MAX_PREVIEW_ROWS + 2) {
            const { done, value } = await reader.read();
            if (done) break;
            accumulated += decoder.decode(value, { stream: true });
            lines = accumulated.split("\n");
            if (lines.length > MAX_PREVIEW_ROWS + 2) break;
          }
          reader.cancel();
        } else {
          // Fallback for browsers without streaming
          const csvText = await csvRes.text();
          lines = csvText.split("\n");
        }

        const filteredLines = lines.filter((line: string) => line.trim());
        if (filteredLines.length === 0) return null;

        const headers = parseCSVLine(filteredLines[0]).map((h: string) => h.replace(/\*+$/, "").trim());
        const data: Record<string, string>[] = [];
        const columnValues: Record<string, string[]> = {};

        headers.forEach((h: string) => {
          columnValues[h] = [];
        });

        for (let i = 1; i < Math.min(filteredLines.length, MAX_PREVIEW_ROWS + 1); i++) {
          const values = parseCSVLine(filteredLines[i]);
          const row: Record<string, string> = {};
          headers.forEach((h: string, idx: number) => {
            row[h] = values[idx] || "";
            columnValues[h].push(values[idx] || "");
          });
          data.push(row);
        }

        setVariableData(columnValues);
        return data;
      } catch (error) {
        console.error("Error downloading/parsing CSV:", error);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [setIsLoading, setVariableData]
  );

  return {
    clientId,
    fetchVariables,
    uploadVariable,
    downloadAndParseCSV,
  };
};

/**
 * Parses a single CSV line handling quoted values
 */
function parseCSVLine(line: string): string[] {
  const result: string[] = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (inQuotes) {
      if (char === '"') {
        if (i + 1 < line.length && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        current += char;
      }
    } else {
      if (char === '"') {
        inQuotes = true;
      } else if (char === ",") {
        result.push(current.trim());
        current = "";
      } else {
        current += char;
      }
    }
  }
  result.push(current.trim());
  return result;
}
